<template>
  <Transition name="fade">
    <div v-if="isOpen" class="blur" @click="isOpen = false"></div>
  </Transition>
  <Transition name="expand">
    <div
      v-if="isOpen"
      class="search"
      :class="{ 'search--expanded': showPredictivePanel }"
    >
      <div class="search__header">
        <div class="search__header-inner grid-container">
          <form class="search__form" @submit.prevent="performSearch">
            <div class="search__input-wrapper">
              <div class="search__input">
                <i class="icon icon-search" />
                <label class="field">
                  <span class="field__label">Search</span>
                  <div class="input">
                    <input
                      ref="searchInput"
                      v-model="searchTerm"
                      class="input__element"
                      type="search"
                      placeholder="Search"
                      role="combobox"
                      aria-autocomplete="list"
                      aria-haspopup="listbox"
                      :aria-expanded="showPredictivePanel"
                      :aria-activedescendant="
                        activeIndex >= 0
                          ? `suggestion-${activeIndex}`
                          : undefined
                      "
                      aria-controls="predictive-search-results"
                      autocomplete="off"
                      @input="handleInput"
                      @keydown="handleKeydown"
                    />
                  </div>
                </label>
              </div>
            </div>
            <Button type="submit" class="search__submit"> Search </Button>
          </form>
          <IconButton form="transparent" icon="cross" @click="isOpen = false" />
        </div>
      </div>
      <div class="search__featured">
        <div class="grid-container search__featured-inner">
          <div
            class="results__grid"
            :class="{
              'results__grid--predictive': showPredictivePanel,
            }"
          >
            <div class="quick-links">
              <div class="group__heading">Trending</div>
              <ul class="nav__list">
                <li
                  v-for="(link, linkIndex) in quickLinks"
                  :key="linkIndex"
                  class="nav__item"
                >
                  <a
                    form="underline-invert"
                    :class="getLinkClass(link)"
                    :href="link.url || null"
                  >
                    {{ link.title }}
                  </a>
                  <div class="badges">
                    <Badge
                      v-for="(badge, index) in link.badges"
                      :key="index"
                      size="small"
                      :theme="badge.theme"
                    >
                      {{ badge.text }}
                    </Badge>
                  </div>
                </li>
              </ul>

              <div v-if="showUsefulLinks" class="quick-links__useful">
                <div class="group__heading group__heading--useful-links">
                  Useful Links
                </div>
                <ul class="nav__list">
                  <li
                    v-for="(link, linkIndex) in usefulLinks"
                    :key="linkIndex"
                    class="nav__item"
                  >
                    <a
                      form="underline-invert"
                      :class="getLinkClass(link)"
                      :href="link.url || null"
                    >
                      {{ link.title }}
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div v-if="showPredictiveNoResults" class="predictive-empty">
              <p class="predictive-empty__message">
                Sorry, we can't find anything matching your search. Try a
                different keyword or explore our trending categories.
              </p>
              <div class="predictive-empty__cards">
                <SearchCard
                  v-for="(link, index) in quickLinks"
                  :key="index"
                  :link="link"
                />
              </div>
            </div>
            <div
              v-else-if="showPredictivePanel && hasSuggestions"
              id="predictive-search-results"
              ref="suggestionsList"
              class="predictive-results"
              role="listbox"
              aria-label="Search suggestions"
            >
              <div
                v-if="suggestions.length > 0"
                class="predictive-results__section"
              >
                <div class="predictive-results__heading">Suggestions</div>
                <button
                  v-for="(suggestion, index) in suggestions"
                  :id="`suggestion-${index}`"
                  :key="'q-' + index"
                  :data-index="index"
                  role="option"
                  :aria-selected="activeIndex === index"
                  class="predictive-results__item predictive-results__query"
                  :class="{
                    'predictive-results__item--active': activeIndex === index,
                  }"
                  @click="selectSuggestion(suggestion)"
                  @mouseenter="activeIndex = index"
                >
                  <i class="icon icon-search predictive-results__icon" />
                  <span v-html="suggestion.styledText" />
                </button>
              </div>

              <div
                v-if="predictiveProducts.length > 0"
                class="predictive-results__section predictive-results__section--products"
              >
                <div class="predictive-results__heading">Products</div>
                <div class="predictive-results__list">
                  <button
                    v-for="(product, index) in predictiveProducts"
                    :id="`suggestion-${suggestions.length + index}`"
                    :key="'p-' + product.id"
                    :data-index="suggestions.length + index"
                    role="option"
                    :aria-selected="activeIndex === suggestions.length + index"
                    class="predictive-results__item predictive-results__product"
                    :class="{
                      'predictive-results__item--active':
                        activeIndex === suggestions.length + index,
                    }"
                    @click="navigateToProduct(product)"
                    @mouseenter="activeIndex = suggestions.length + index"
                  >
                    <img
                      v-if="product.featuredImage"
                      :src="getPredictiveProductImageUrl(product)"
                      :alt="product.featuredImage.altText || product.title"
                      class="predictive-results__product-image"
                      width="80"
                      loading="lazy"
                    />
                    <div
                      v-else
                      class="predictive-results__product-image predictive-results__product-image--placeholder"
                    />
                    <div class="predictive-results__product-info">
                      <span class="nav__link predictive-results__product-title">
                        {{ product.title }}
                      </span>
                      <span class="predictive-results__product-price">
                        {{ formatProductPrice(product) }}
                      </span>
                    </div>
                  </button>
                </div>
              </div>

              <div class="predictive-results__footer">
                <Button
                  type="button"
                  width="full"
                  class="predictive-results__view-all"
                  @click="performSearch"
                >
                  View all results
                </Button>
              </div>
            </div>
            <div
              v-else
              class="search__items search__items--featured search__items--fullbleed"
            >
              <SearchCard
                v-for="(link, index) in quickLinks"
                :key="index"
                :link="link"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import IconButton from "@/components/IconButton.vue";
import Button from "@/components/ButtonV3.vue";
import SearchCard from "@/components/navigation/search/SearchCard.vue";
import predictiveSearchQuery from "@/graphql/queries/PredictiveSearch.gql";
import searchQuery from "@/graphql/queries/SearchV2.gql";
import searchProductsByIdsQuery from "@/graphql/queries/SearchProductsByIds.gql";
import { getFeatureToggles } from "@/helpers/featureToggles";
import { getSearchNavigationLinks } from "@/helpers/searchNavigationLinks";
import {
  nostoPredictiveSearch,
  nostoProductSearch,
  recordNostoSearchSubmit,
  recordNostoSearchClick,
  setNostoKeywordFlag,
  toNostoProductId,
} from "@/helpers/nostoSearch";

export default {
  name: "SearchNavigation",
  components: {
    IconButton,
    Button,
    SearchCard,
  },
  inject: ["mq"],
  props: {
    highlightTitles: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchTerm: "",
      suggestions: [],
      predictiveProducts: [],
      showSuggestions: false,
      isPredictiveLoading: false,
      predictiveFetched: false,
      activeIndex: -1,
      predictiveDebounceTimer: null,
      usefulLinks: [
        {
          title: "Returns & Refunds",
          url: "https://help-gb.ratandboa.com/en-US#category-433380",
        },
        {
          title: "Shipping and Delivery",
          url: "https://help-gb.ratandboa.com/en-US/articles/shipping-delivery-433382",
        },
        {
          title: "Members Rewards",
          url: "/pages/members",
        },
      ],
    };
  },
  computed: {
    ...mapState("search", ["isSearchOpen"]),
    ...mapGetters("app", ["currencyCode"]),
    useNostoSearch() {
      const { nostoSearchEnabled = false } = getFeatureToggles();
      return nostoSearchEnabled;
    },
    isOpen: {
      get() {
        return this.isSearchOpen;
      },
      set(val) {
        return this.setSearchOpen(val);
      },
    },
    hasSuggestions() {
      return this.suggestions.length > 0 || this.predictiveProducts.length > 0;
    },
    totalSuggestionItems() {
      return this.suggestions.length + this.predictiveProducts.length;
    },
    showPredictivePanel() {
      return this.searchTerm.trim().length >= 2;
    },
    showPredictiveNoResults() {
      return (
        this.showPredictivePanel &&
        this.predictiveFetched &&
        !this.isPredictiveLoading &&
        !this.hasSuggestions
      );
    },
    showUsefulLinks() {
      if (!this.showPredictivePanel) {
        return true;
      }

      return this.mq.mdPlus;
    },
    quickLinks() {
      return getSearchNavigationLinks(this.currencyCode);
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      } else {
        this.searchTerm = "";
        this.clearPredictive();
      }
    },
  },
  beforeUnmount() {
    clearTimeout(this.predictiveDebounceTimer);
  },
  methods: {
    ...mapActions("search", ["setSearchOpen"]),
    getLinkClass(link) {
      const baseClass = "nav__link";
      if (this.highlightTitles || link?.highlightRed === true) {
        return `${baseClass} nav__link--highlight`;
      }
      return baseClass;
    },
    handleInput() {
      const term = this.searchTerm.trim();
      clearTimeout(this.predictiveDebounceTimer);
      this.predictiveFetched = false;

      if (term.length < 2) {
        this.clearPredictive();
        return;
      }

      this.predictiveDebounceTimer = setTimeout(() => {
        this.fetchPredictive(term);
      }, 300);
    },
    async fetchPredictive(term) {
      if (this.useNostoSearch) {
        return this.fetchNostoPredictive(term);
      }

      this.isPredictiveLoading = true;
      try {
        const { data } = await this.$apollo.query({
          query: predictiveSearchQuery,
          variables: { query: term, limit: 10 },
        });
        this.suggestions = data.predictiveSearch.queries || [];
        this.predictiveProducts = data.predictiveSearch.products || [];

        if (this.predictiveProducts.length === 0) {
          await this.applyShopifyPredictiveFallback(term);
        }

        this.showSuggestions = this.hasSuggestions;
        this.activeIndex = -1;
      } catch (error) {
        console.error("Predictive search error:", error);
        await this.applyShopifyPredictiveFallback(term);
      } finally {
        this.isPredictiveLoading = false;
        this.predictiveFetched = true;
      }
    },
    async fetchNostoPredictive(term) {
      this.isPredictiveLoading = true;
      try {
        const { products, keywords, redirect } = await nostoPredictiveSearch({
          currencyCode: this.currencyCode,
          query: term,
          size: 10,
          keywordSize: 5,
          track: "autocomplete",
        });

        // A query-level redirect rule should forward immediately rather than
        // showing predictive results for a term that has no product matches.
        if (redirect) {
          this.clearPredictive();
          window.location.href = redirect;
          return;
        }

        this.suggestions = keywords.map((hit) => ({
          text: hit.keyword,
          styledText: hit._highlight?.keyword || hit.keyword,
          redirect: hit._redirect || null,
        }));

        this.predictiveProducts = products.map((hit) =>
          this.mapNostoPredictiveProduct(hit)
        );

        if (this.predictiveProducts.length === 0) {
          this.predictiveProducts = await this.fetchNostoSearchProducts(term);
        }

        if (this.predictiveProducts.length === 0) {
          await this.applyShopifyPredictiveFallback(term);
        }

        this.showSuggestions = this.hasSuggestions;
        this.activeIndex = -1;
      } catch (error) {
        console.error("Nosto predictive search error:", error);
        await this.applyShopifyPredictiveFallback(term);
      } finally {
        this.isPredictiveLoading = false;
        this.predictiveFetched = true;
      }
    },
    async applyShopifyPredictiveFallback(term) {
      this.predictiveProducts = await this.fetchShopifySearchProducts(term);
      this.showSuggestions = this.hasSuggestions;
    },
    async fetchShopifySearchProducts(term) {
      const { data } = await this.$apollo.query({
        query: searchQuery,
        fetchPolicy: "network-only",
        variables: {
          searchQuery: term,
          first: 10,
          sort: "RELEVANCE",
        },
      });

      if (!data?.search?.totalCount) {
        return [];
      }

      return (data.search.edges || []).map(({ node }) =>
        this.mapSearchProductToPredictive(node)
      );
    },
    async fetchNostoSearchProducts(term) {
      const { productIds, total } = await nostoProductSearch({
        currencyCode: this.currencyCode,
        query: term,
        size: 10,
        from: 0,
      });

      if (!total || !productIds.length) {
        return [];
      }

      const products = await this.hydrateProductIds(productIds);

      return products.map((product) =>
        this.mapSearchProductToPredictive(product)
      );
    },
    async hydrateProductIds(productIds) {
      if (!productIds.length) {
        return [];
      }

      const ids = productIds.map((id) => `gid://shopify/Product/${id}`);

      const { data } = await this.$apollo.query({
        query: searchProductsByIdsQuery,
        variables: { ids },
      });

      return (data?.nodes || []).filter(Boolean);
    },
    mapSearchProductToPredictive(node) {
      return {
        id: node.id,
        title: node.title,
        handle: node.handle,
        onlineStoreUrl: node.onlineStoreUrl,
        featuredImage: this.resolvePredictiveProductImage(node),
        priceRange: node.priceRange,
      };
    },
    resolvePredictiveProductImage(node) {
      if (node.featuredImage?.url) {
        return {
          url: node.featuredImage.url,
          altText: node.featuredImage.altText || node.title,
          preTransformed: false,
        };
      }

      const imageNode =
        node.ecomImages?.references?.edges?.[0]?.node?.image ||
        node.campaignImages?.references?.edges?.[0]?.node?.image ||
        node.altCampaignImages?.reference?.image ||
        node.flatlayImages?.references?.edges?.[0]?.node?.image ||
        node.images?.edges?.[0]?.node;

      if (!imageNode) {
        return null;
      }

      const url =
        imageNode.smallMedia2x ||
        imageNode.smallMedia ||
        imageNode.mediumMedia ||
        imageNode.largeMedia;

      if (!url) {
        return null;
      }

      return {
        url,
        altText: imageNode.altText || node.title,
        preTransformed: true,
      };
    },
    mapNostoPredictiveProduct(hit) {
      return {
        id: hit.productId,
        title: hit.name,
        handle: null,
        onlineStoreUrl: hit.url,
        featuredImage: hit.imageUrl
          ? { url: hit.imageUrl, altText: hit.name }
          : null,
        priceRange: {
          minVariantPrice: {
            amount: hit.price,
            currencyCode: hit.priceCurrencyCode || this.currencyCode,
          },
        },
      };
    },
    clearPredictive() {
      clearTimeout(this.predictiveDebounceTimer);
      this.suggestions = [];
      this.predictiveProducts = [];
      this.showSuggestions = false;
      this.activeIndex = -1;
      this.isPredictiveLoading = false;
      this.predictiveFetched = false;
    },
    selectSuggestion(suggestion) {
      if (suggestion.redirect) {
        this.clearPredictive();
        window.location.href = suggestion.redirect;
        return;
      }

      this.searchTerm = suggestion.text;
      this.clearPredictive();

      const term = this.searchTerm.trim();
      if (!term) return;

      // Keyword clicks are not organic input submits, so no recordSearchSubmit
      // here; instead flag the term so the SERP tracks it as a keyword search.
      if (this.useNostoSearch) {
        setNostoKeywordFlag(term);
      }
      this.goToSearchPage(term);
    },
    navigateToProduct(product) {
      this.clearPredictive();

      if (this.useNostoSearch) {
        recordNostoSearchClick("autocomplete", {
          productId: toNostoProductId(product.id),
          url: product.onlineStoreUrl || `/products/${product.handle}`,
        });
      }

      window.location.href =
        product.onlineStoreUrl || `/products/${product.handle}`;
    },
    formatProductPrice(product) {
      const { amount, currencyCode } = product.priceRange.minVariantPrice;
      return this.$filters.toCurrency(amount, currencyCode, 0);
    },
    getPredictiveProductImageUrl(product) {
      const { url, preTransformed } = product.featuredImage || {};
      if (!url) return "";

      if (preTransformed) {
        return url;
      }

      const imageWidth = 160;
      const separator = url.includes("?") ? "&" : "?";

      return `${url}${separator}width=${imageWidth}`;
    },
    handleKeydown(event) {
      if (!this.showPredictivePanel || !this.hasSuggestions) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          this.activeIndex = Math.min(
            this.activeIndex + 1,
            this.totalSuggestionItems - 1
          );
          this.scrollActiveIntoView();
          break;
        case "ArrowUp":
          event.preventDefault();
          this.activeIndex = Math.max(this.activeIndex - 1, -1);
          this.scrollActiveIntoView();
          break;
        case "Enter":
          if (this.activeIndex >= 0) {
            event.preventDefault();
            if (this.activeIndex < this.suggestions.length) {
              this.selectSuggestion(this.suggestions[this.activeIndex]);
            } else {
              const productIdx = this.activeIndex - this.suggestions.length;
              this.navigateToProduct(this.predictiveProducts[productIdx]);
            }
          }
          break;
        case "Escape":
          this.clearPredictive();
          break;
      }
    },
    scrollActiveIntoView() {
      this.$nextTick(() => {
        const el = this.$refs.suggestionsList?.querySelector(
          `[data-index="${this.activeIndex}"]`
        );
        el?.scrollIntoView({ block: "nearest" });
      });
    },
    performSearch() {
      this.clearPredictive();
      const term = this.searchTerm.trim();
      if (!term) return;

      // Mark the query as an organic search before navigating to the SERP.
      if (this.useNostoSearch) {
        recordNostoSearchSubmit(term);
      }
      this.goToSearchPage(term);
    },
    goToSearchPage(term) {
      window.location.href = `/search?q=${encodeURIComponent(term)}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.icon-search {
  font-size: 24px;
}

.field {
  .input {
    &__element {
      width: 100%;
      padding: 10px;
      border: 0;
      outline: none;
      @include editorial-body(16px);

      &::placeholder {
        color: #000;
        opacity: 1;
      }
    }
  }

  &__label {
    @extend .u__visually-hidden;
  }
}

.search {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background-color: #fff;
  max-height: 100dvh;
  overflow-y: auto;

  &--expanded {
    display: flex;
    flex-direction: column;
    height: 100dvh;
    max-height: 100dvh;
    overflow: hidden;
  }
}

.search__header {
  flex-shrink: 0;
  border-bottom: 1px solid #e4e4e4;
  padding: 18px 0;

  .search__header-inner {
    display: flex;
    gap: 40px;
    align-items: center;
    width: 100%;

    .search__form {
      display: flex;
      gap: 40px;
      align-items: center;
      flex: 1;
      min-width: 0;
    }

    .search__input-wrapper {
      flex: 1;
      min-width: 0;
    }

    .search__submit {
      flex-shrink: 0;
    }

    > :last-child {
      flex-shrink: 0;
    }
  }
}

.search__input {
  display: flex;
  align-items: center;
  gap: 12px;

  .field {
    flex: 1 0 auto;
  }

  input[type="search"]::-webkit-search-decoration,
  input[type="search"]::-webkit-search-cancel-button,
  input[type="search"]::-webkit-search-results-button,
  input[type="search"]::-webkit-search-results-decoration {
    -webkit-appearance: none;
  }
}

.search__input-wrapper {
  flex: 1;
  min-width: 0;
}

.search__featured {
  padding: 28px 0 60px;

  .search--expanded & {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}

.search__featured-inner {
  .search--expanded & {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
}

.predictive-empty {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  @include respond-above(md) {
    padding-left: 40px;
  }

  &__message {
    max-width: 340px;
    margin: 0 auto 40px;
    text-align: center;
    @include editorial-body(14px);
    line-height: 150%;

    @include respond-above(md) {
      margin-bottom: 48px;
    }
  }

  &__cards {
    display: flex;
    gap: 20px;
    overflow-x: auto;
    padding-bottom: 12px;
    padding-left: 0;

    @include respond-above(md) {
      padding-left: 0;
      margin-right: calc(-1 * (100vw - 100%) / 2);
    }

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.05);
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.35);
    }

    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.35) rgba(0, 0, 0, 0.05);

    & > * {
      flex: 0 0 260px;
      width: 260px;
    }
  }
}

.predictive-results {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;

  @include respond-above(md) {
    padding-left: 40px;
  }

  &__section {
    flex-shrink: 0;

    &--products {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
  }

  &__heading {
    @include utility-body(15px);
    margin-bottom: 8px;
    color: $color-grey-dark;
    line-height: 150%;
  }

  &__list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    margin: 0 -20px;
    padding: 0 20px;

    @include respond-above(md) {
      margin: 0;
      padding: 0;
    }
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 8px 0;
    border: 0;
    background: none;
    cursor: pointer;
    text-align: left;
    @include editorial-body(14px);
    color: #000;
    transition: background-color 0.15s ease;

    &:hover,
    &--active {
      background-color: #f5f5f5;
    }
  }

  &__icon {
    font-size: 14px;
    color: $color-grey-dark;
    flex-shrink: 0;
  }

  &__query {
    padding: 10px 0;

    :deep(b) {
      font-weight: 400;
    }

    :deep(span:not(b)) {
      font-weight: 600;
    }
  }

  &__product {
    align-items: flex-start;
  }

  &__product-image {
    width: 80px;
    height: auto;
    object-fit: cover;
    flex-shrink: 0;
    background-color: #f5f5f5;

    &--placeholder {
      display: block;
      aspect-ratio: 2 / 3;
    }
  }

  &__product-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__product-title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      text-decoration: none;
    }
  }

  &__product-price {
    @include utility-body(12px);
    color: $color-grey-dark;
  }

  &__footer {
    flex-shrink: 0;
    padding-top: 24px;
    margin-top: auto;
  }

  &__view-all {
    text-transform: uppercase;
  }
}

.search__submit {
  display: none;

  @include respond-above(md) {
    display: block;
  }
}

.expand-enter-active,
.expand-leave-active {
  transition: opacity 0.25s ease, transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.results__grid {
  display: flex;
  flex-direction: column;
  row-gap: 60px;
  flex: 1;
  min-height: 0;

  @include respond-above(md) {
    flex-direction: row;
    align-items: stretch;
    position: relative;
  }

  &--predictive {
    row-gap: 24px;

    @include respond-below(md) {
      row-gap: 12px;

      .quick-links {
        flex: 0 0 auto;

        .nav__list {
          gap: 4px;
        }
      }
    }

    @include respond-above(md) {
      flex-direction: row;
      align-items: stretch;
      row-gap: 0;
    }

    .quick-links {
      flex-shrink: 0;

      @include respond-above(md) {
        flex: 0 0 200px;
        min-height: 0;
        border-right: 1px solid #e4e4e4;
        padding-right: 40px;
        box-sizing: content-box;
      }
    }
  }

  .quick-links {
    flex: 0 0 160px;
    z-index: 1;

    @include respond-above(md) {
      border-right: 1px solid #e4e4e4;
      padding-right: 40px;
      box-sizing: content-box;
      flex: 0 0 200px;
    }

    .group__heading {
      @include utility-body(15px);
      margin-bottom: 8px;
      color: $color-grey-dark;
      line-height: 150%;

      &.group__heading--useful-links {
        margin-top: 60px;

        @include respond-above(md) {
          margin-top: 100px;
        }
      }
    }

    .nav__list {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .nav__item {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }
  }

  .nav__link {
    @include editorial-body(14px);
    color: #000;
    text-decoration: none;
    line-height: 150%;

    &:hover {
      text-decoration: underline;
    }

    &--highlight {
      color: $color-red;
    }
  }

  .search__items--fullbleed {
    &.search__items--featured {
      margin-right: calc(-1 * (100vw - 100%) / 2);
    }
  }
}

.search__items {
  display: none;

  @include respond-above(md) {
    display: flex;
    gap: 20px;
    padding-left: 40px;
  }

  &--featured {
    column-gap: 20px;
    overflow-y: hidden;
    overflow-x: scroll;
    flex-direction: row;
    flex-wrap: nowrap;
    padding-bottom: 12px;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.05);
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.35);
    }

    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.35) rgba(0, 0, 0, 0.05);

    & > * {
      flex: 0 0 260px;
      width: 260px;
    }
  }
}

.blur {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 4;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur($blur-amount);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.grid-container {
  padding: 0 20px;

  @include respond-above(md) {
    padding: 0 40px;
  }
}
</style>
